import { css, cva, cx, sva } from '@mildang/styled-system/css';
import { SystemStyleObject } from '@mildang/styled-system/types';
import { cloneElement, ElementType, isValidElement, ReactElement, ReactNode } from 'react';
import IconButton from 'components/molecules/IconButton/LegacyIconButton';
import CloseIcon from '@mildang/icons/react/close';
import WarningOutline from '@mildang/icons/react/warning-outline';
import InfoOutline from '@mildang/icons/react/info-outline';
import ErrorOutline from '@mildang/icons/react/error-outline';
import CheckIcon from '@mildang/icons/react/check';

const alert = sva({
  slots: ['root', 'titleAndMessage', 'title', 'message', 'action'],
  base: {
    root: {
      padding: '12px 16px',
      borderRadius: 'md',
      display: 'grid',
      gridTemplateColumns: '[1fr auto auto]',
      width: '[336px]',
      minWidth: '[286px]',
      maxWidth: '[400px]',
      boxShadow: '4',
    },
    titleAndMessage: {
      display: 'flex',
      gap: '8px',
    },
    title: { textStyle: 'subtitle3-B', lineHeight: '20px !' },
    message: { textStyle: 'body4-R' },
    action: { display: 'flex', alignItems: 'center', gap: '8px' },
  },
  defaultVariants: {
    severity: 'success',
  },
  variants: {
    severity: {
      error: {
        root: {
          border: '1px solid token(colors.error.7)',
          backgroundColor: 'error.2',
        },
        title: {
          color: 'error.10',
        },
        message: {
          color: 'error.10',
        },
        action: {
          color: 'error.7',
        },
      },
      warning: {
        root: {
          border: '1px solid token(colors.warning.7)',
          backgroundColor: 'warning.2',
        },
        title: {
          color: 'warning.10',
        },
        message: {
          color: 'warning.10',
        },
        action: {
          color: 'warning.7',
        },
      },
      info: {
        root: {
          border: '1px solid token(colors.info.7)',
          backgroundColor: 'info.2',
        },
        title: {
          color: 'info.10',
        },
        message: {
          color: 'info.10',
        },
        action: {
          color: 'info.7',
        },
      },
      success: {
        root: {
          border: '1px solid token(colors.success.7)',
          backgroundColor: 'success.2',
        },
        title: {
          color: 'success.10',
        },
        message: {
          color: 'success.10',
        },
        action: {
          color: 'success.7',
        },
      },
      basic: {
        root: {
          border: '1px solid token(colors.common.5)',
          backgroundColor: 'common.2',
        },
        title: {
          color: 'common.10',
        },
        message: {
          color: 'common.10',
        },
        action: {
          color: 'common.mediumBg.normal',
        },
      },
    },
    inline: {
      true: {
        root: {
          width: '[100%]',
          maxWidth: '[100%]',
          boxShadow: 'none',
        },
      },
    },
    multiline: {
      true: {
        title: {
          paddingTop: '2px',
        },
      },
      false: {
        root: {
          padding: '8px 16px',
        },
        titleAndMessage: {
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
  },
});

const iconStyle = cva({
  base: {
    fontSize: '[20px]',
  },
  variants: {
    severity: {
      error: {
        color: 'error.7',
      },
      warning: { color: 'warning.7' },
      info: { color: 'info.7' },
      success: { color: 'success.7' },
      basic: { color: 'common.mediumBg.normal' },
    },
    multiline: {
      true: {
        paddingTop: '2px',
      },
    },
  },
});

export interface AlertProps {
  title?: ReactNode;
  message?: ReactNode;
  severity?: 'error' | 'warning' | 'info' | 'success' | 'basic';
  infinite?: boolean;
  onClose?: () => void;
  closeText?: string;
  inline?: boolean;
  action?: ReactNode;
  actionTitle?: string;
  actionAltText?: string;
  className?: string;
  icon?: ReactNode;
  hideIcon?: boolean;
  panda?: SystemStyleObject;
}

const Alert = (props: AlertProps) => {
  const {
    title,
    message,
    severity = 'success',
    infinite = false,
    closeText = '닫기',
    onClose,
    inline = false,
    action: actionProp,
    className,
    icon: iconProp,
    hideIcon = false,
    panda,
    ...others
  } = props;

  const multiline = Boolean(title && message) ?? false;

  let icon: ReactNode = null;
  /**
   * @see https://www.figma.com/design/rFLIV3B2e7K2z074q18BIr/%F0%9F%8E%A8-New)-IDS-(IHFB-Core-Design-System)?node-id=34865-218089&m=dev
   */
  if (severity === 'error') {
    icon = <ErrorOutline className={iconStyle({ severity: 'error', multiline })} aria-hidden />;
  } else if (severity === 'warning') {
    icon = <WarningOutline className={iconStyle({ severity: 'warning', multiline })} aria-hidden />;
  } else if (severity === 'info') {
    icon = <InfoOutline className={iconStyle({ severity: 'info', multiline })} aria-hidden />;
  } else if (severity === 'success') {
    icon = <CheckIcon className={iconStyle({ severity: 'success', multiline })} aria-hidden />;
  } else if (severity === 'basic') {
    icon = <InfoOutline className={iconStyle({ severity: 'basic', multiline })} aria-hidden />;
  } else if (iconProp) {
    icon = iconProp;
  }

  let action: ReactElement | undefined = undefined;
  if (actionProp && isValidElement(actionProp)) {
    action = cloneElement(actionProp, {
      // @ts-ignore
      className: actionProp.props.className,
      onClick: (e: any) => {
        (actionProp.props as any).onClick?.(e);
        if (onClose) {
          e.stopPropagation();
          onClose();
        }
      },
    });
  }

  const classes = alert({ severity, inline, multiline });

  const Component = inline ? 'div' : ('output' as ElementType);

  return (
    <Component {...others} className={cx(classes.root, css(panda), className)}>
      <div className={classes.titleAndMessage}>
        {hideIcon ? null : icon}
        <div className={css({ flex: 1 })}>
          <p className={classes.title}>{title}</p>
          <p className={classes.message}>{message}</p>
        </div>
      </div>
      <div className={classes.action}>
        {action}
        {onClose && (
          <IconButton size="small" aria-label={closeText} title={closeText} color="inherit" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </div>
    </Component>
  );
};

export default Alert;
